import { createContext, type Dispatch, type PropsWithChildren, useContext, useEffect, useReducer, useRef } from 'react';
import { initializeDB, manuallyDeleteDatabase } from '../database/db';
import { type DrizzleDB } from '../database/types';
import storage from '../helpers/storage';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import useUser from '../hooks/useUser';
import { type DatabaseAction, databaseActions, type DatabaseState, initialDatabaseState } from './context';
import { checkDatabaseSupport, isReactNativeWebView, setDebugDb } from './databaseUtils';
import { databaseReducer } from './reducers/databaseReducer';
interface PersistedState {
  disabled: boolean;
  error: string | null;
  showPopup: boolean;
  hashingDisabled: boolean;
}
export type ErrorReporter = (error: string, stack: string, source?: 'client' | 'mobile') => Promise<void>;
export type HashRecord = {
  id: string;
  tableName: string;
  hash: string;
};
export type HashSyncResult = {
  storedHashes: string[];
};
export type HashSyncer = (params: {
  upserts?: HashRecord[];
  deletes?: string[];
}) => Promise<HashSyncResult>;
export interface DatabaseContextValue {
  state: DatabaseState;
  dispatch: Dispatch<DatabaseAction>;
  userId: number | undefined;
  acknowledgePopup: () => Promise<void>;
  triggerError: (message: string) => Promise<void>;
  retryDatabase: (onReady?: (db: DrizzleDB) => void) => Promise<void>;
  resetDatabase: () => Promise<void>;
  closeDatabase: () => void;
  deleteDatabase: () => Promise<void>;
  reportErrorToServer?: ErrorReporter;
  syncHashes?: HashSyncer;
  setHashingDisabled: (disabled: boolean) => Promise<void>;
  /**
   * Run any migrations that were deferred by migrationGuard. Resolves when
   * migrations complete. Throws if no migrations are pending or initializeDB
   * did not defer them.
   */
  applyPendingMigrations: () => Promise<void>;
}
export interface DatabaseProviderProps extends PropsWithChildren {
  reportErrorToServer?: ErrorReporter;
  client?: WrappedTRPCClient;
  /** If true, hash syncing is completely disabled (no server calls) */
  disableHashing?: boolean;
  /**
   * Optional pre-migration guard — called only when pending migrations are
   * detected. Returning false defers migrate() until applyPendingMigrations()
   * is invoked. Mobile uses this to wait for a vault unlock + backup.
   */
  migrationGuard?: (userId: number) => boolean | Promise<boolean>;
}
const DatabaseContext = createContext<DatabaseContextValue | undefined>(undefined);
const STORAGE_KEY = 'database_state';
export const DatabaseProvider: React.FC<DatabaseProviderProps> = ({
  children,
  reportErrorToServer,
  client,
  disableHashing = false,
  migrationGuard
}) => {
  // Only provide syncHashes if hashing is not disabled
  const syncHashes: HashSyncer | undefined = !disableHashing && client ? async ({
    upserts = [],
    deletes = []
  }) => {
    const [error, result] = await client.localDataHash.upsert.mutate({
      upserts,
      deletes
    });
    if (error) {
      return {
        storedHashes: []
      };
    }
    return {
      storedHashes: result?.storedHashes ?? []
    };
  } : undefined;
  const {
    currentUser
  } = useUser();
  const userId = currentUser?.id;
  const [state, dispatch] = useReducer(databaseReducer, initialDatabaseState);
  const {
    db,
    destroy,
    disabled,
    isInitializing
  } = state;
  // Holds the deferred-migration callback returned by initializeDB. Cleared
  // once migrations are applied or the DB is torn down.
  const applyPendingMigrationsRef = useRef<(() => Promise<void>) | null>(null);
  const teardownDB = () => {
    destroy?.();
    applyPendingMigrationsRef.current = null;
    dispatch({
      type: databaseActions.SET_MULTIPLE,
      payload: {
        db: null,
        destroy: null,
        isInitializing: false,
        pendingMigrations: false
      }
    });
    setDebugDb(null);
  };
  const createDB = async (onReady?: (database: DrizzleDB) => void): Promise<void> => {
    teardownDB();
    dispatch({
      type: databaseActions.SET_IS_INITIALIZING,
      payload: true
    });
    try {
      const result_0 = await initializeDB(userId!, {
        migrationGuard
      });
      applyPendingMigrationsRef.current = result_0.applyPendingMigrations ?? null;
      dispatch({
        type: databaseActions.SET_MULTIPLE,
        payload: {
          db: result_0.db,
          destroy: result_0.destroy,
          isInitializing: false,
          pendingMigrations: result_0.pendingMigrations
        }
      });
      setDebugDb(result_0.db);
      // Only signal readiness when the DB is actually usable (migrations applied).
      if (!result_0.pendingMigrations) {
        onReady?.(result_0.db);
      }
    } catch (err) {
      dispatch({
        type: databaseActions.SET_IS_INITIALIZING,
        payload: false
      });
      await triggerError(`Database initialization failed: ${err}`);
    }
  };
  const applyPendingMigrations = async (): Promise<void> => {
    const run = applyPendingMigrationsRef.current;
    if (!run) {
      throw new Error('No pending migrations to apply');
    }
    await run();
    applyPendingMigrationsRef.current = null;
    dispatch({
      type: databaseActions.SET_MULTIPLE,
      payload: {
        pendingMigrations: false
      }
    });
  };
  const persistState = async (next: Partial<PersistedState>) => {
    const updated = {
      disabled: state.disabled,
      error: state.error,
      showPopup: state.showPopup,
      hashingDisabled: state.hashingDisabled,
      ...next
    };
    await storage.setItem(STORAGE_KEY, JSON.stringify(updated));
    dispatch({
      type: databaseActions.SET_MULTIPLE,
      payload: {
        disabled: updated.disabled,
        error: updated.error,
        showPopup: updated.showPopup,
        hashingDisabled: updated.hashingDisabled
      }
    });
  };
  const setHashingDisabled = async (hashingDisabled: boolean) => {
    await persistState({
      hashingDisabled
    });
  };
  const triggerError = async (message: string) => {
    teardownDB();
    await persistState({
      disabled: true,
      error: message,
      showPopup: true
    });
  };
  const acknowledgePopup = async () => {
    await persistState({
      showPopup: false
    });
  };
  const initializeDatabase = async (skipDisabledCheck = false) => {
    // skipDisabledCheck is used when we just dispatched SET_DISABLED: false
    // but the state hasn't updated yet (React batches state updates)
    if (isInitializing || !skipDisabledCheck && disabled || db) return;

    // Inside the mobile app's WebView the web client only renders the Cesium
    // map — the local DB is unused and OPFS init fails in WKWebView. Disable it
    // silently (no error modal) instead of trying and surfacing the failure.
    if (isReactNativeWebView()) {
      await persistState({
        disabled: true,
        error: null,
        showPopup: false
      });
      return;
    }
    const {
      supported,
      reason
    } = await checkDatabaseSupport();
    if (!supported) {
      await triggerError(reason || 'Database not supported');
      return;
    }
    await createDB();
  };
  const retryDatabase = async (onReady_0?: (database: DrizzleDB) => void) => {
    await persistState({
      disabled: false,
      error: null,
      showPopup: false
    });
    await createDB(onReady_0);
  };
  const resetDatabase = async () => {
    teardownDB();
    await manuallyDeleteDatabase(userId!);
    await persistState({
      disabled: false,
      error: null,
      showPopup: false
    });
    await createDB();
  };
  const closeDatabase = () => {
    teardownDB();
  };
  const deleteDatabase = async () => {
    teardownDB();
    try {
      await manuallyDeleteDatabase(userId!);
      await persistState({
        disabled: true,
        error: null,
        showPopup: false
      });
    } catch (err_0) {
      console.error('Failed to delete database:', err_0);
    }
  };
  useEffect(() => {
    const initialize = async () => {
      if (!userId) {
        if (db) teardownDB();
        dispatch({
          type: databaseActions.SET_DISABLED,
          payload: true
        });
        return;
      }
      try {
        const stored = await storage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as PersistedState;
          if (parsed.error) {
            await storage.setItem(STORAGE_KEY, JSON.stringify({
              ...parsed,
              error: null,
              showPopup: false
            }));
            dispatch({
              type: databaseActions.SET_MULTIPLE,
              payload: {
                error: null,
                showPopup: false
              }
            });
          }
          dispatch({
            type: databaseActions.SET_MULTIPLE,
            payload: {
              disabled: parsed.disabled,
              hashingDisabled: parsed.hashingDisabled ?? false
            }
          });
          if (!parsed.disabled && !db && !isInitializing) {
            // Pass true to skip the disabled check since we just dispatched
            // SET_DISABLED: false but state hasn't updated yet
            await initializeDatabase(true);
          }
        } else {
          // Default to enabled for new users
          await storage.setItem(STORAGE_KEY, JSON.stringify({
            disabled: false,
            error: null,
            showPopup: false,
            hashingDisabled: false
          }));
          await initializeDatabase(true);
        }
      } catch (err_1) {
        console.error('Error loading DB state:', err_1);
      }
    };
    initialize();
    return () => {
      teardownDB();
    };
  }, [userId]);
  return <DatabaseContext.Provider value={{
    state,
    dispatch,
    userId,
    acknowledgePopup,
    triggerError,
    retryDatabase,
    resetDatabase,
    closeDatabase,
    deleteDatabase,
    reportErrorToServer,
    syncHashes,
    setHashingDisabled,
    applyPendingMigrations
  }}>
      {children}
    </DatabaseContext.Provider>;
};
export const useDatabaseContext = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error("useDatabaseContext must be used within DatabaseProvider");
  }
  return context;
};